/**
 * projectAnnualProfit — 12/02/2026
 *
 * Source unique de vérité pour l'extrapolation du bénéfice annuel et de
 * l'IS estimé pour l'année en cours. Introduit pour répondre au retour
 * user prod : « L'IS semble trop élevé au début d'année ».
 *
 * Diagnostic : l'ancienne formule `annualProfit × (12 / month)` amplifie
 * violemment le résultat aux mois 1 et 2 (multiplicateurs 12 et 6). Un
 * pic de profit ponctuel en janvier faisait exploser l'IS annuel projeté
 * et rendait la KPI "IS estimé annuel" alarmante avant même le premier
 * acompte.
 *
 * Correction : lissage début d'année via un dénominateur plancher à 3.
 *   - Mois 1 : ×4 (au lieu de ×12) → prévision conservatrice
 *   - Mois 2 : ×4 (au lieu de ×6)  → idem
 *   - Mois 3 : ×4 (identique)
 *   - Mois 4-12 : ×(12/mois) comme avant (l'échantillon est suffisant)
 *
 * Effet acompte IS : les acomptes (mois 3/6/9/12) sont inchangés côté
 * mécanique — le premier acompte tombe en T1 (mois 3) avec un déno-
 * minateur déjà égal à 3. Seule la KPI "IS estimé annuel" affichée
 * pendant janvier-février est adoucie.
 */
import { calculateIS } from '../data/taxes';

/**
 * Calcule l'extrapolation lissée du bénéfice annuel et de l'IS.
 *
 * @param {Object} state — state.annualProfit + state.month (Zustand slice)
 * @returns {{ annualProfit: number, annualIS: number, damped: boolean }}
 */
export function projectAnnualProfit(state) {
  const annualProfit = state?.annualProfit || 0;
  const rawMonth = state?.month || 1;
  const month = Math.max(1, rawMonth);
  // Plancher à 3 : les mois 1-2 n'amplifient plus l'extrapolation.
  const denominator = Math.max(month, 3);
  const projected = Math.max(0, annualProfit * (12 / denominator));
  return {
    annualProfit: Math.round(projected),
    annualIS: calculateIS(projected),
    damped: month < 3,
  };
}

// Petit helper pratique quand on n'a besoin QUE de l'IS estimé.
export function projectAnnualIS(state) {
  return projectAnnualProfit(state).annualIS;
}
